import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Loader2, AlertCircle } from "lucide-react";
import { api, API, authedFetch } from "../lib/api";

/**
 * PhotoView — authenticated full-screen photo viewer.
 *
 * Route: /view/photo/:path  (path is URL-encoded; wildcard match, may contain slashes)
 *
 * This is the target of the "VEDI" links inside generated PDFs, so admins/clients
 * see photos inside the app with proper auth + mobile-friendly (Bearer token).
 */
export default function PhotoView() {
  const { "*": rawPath } = useParams();
  const navigate = useNavigate();
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [meta, setMeta] = useState(null); // {owner_client, item_type, item_id, item_label}

  const path = decodeURIComponent(rawPath || "");

  useEffect(() => {
    let cancelled = false;
    let localBlobUrl = null;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await authedFetch(`${API}/files/${path}`);
        if (!res.ok) {
          if (res.status === 404) setError("Foto non trovata o non più disponibile.");
          else if (res.status === 401) setError("Accesso negato. Effettua il login.");
          else setError(`Errore HTTP ${res.status}`);
          setLoading(false);
          return;
        }
        const b = await res.blob();
        localBlobUrl = URL.createObjectURL(b);
        if (!cancelled) setBlobUrl(localBlobUrl);
      } catch (e) {
        if (!cancelled) setError(e.message || "Errore di rete");
      }
      setLoading(false);
    })();
    // Fetch context (which item this photo belongs to) — nice to have, non-blocking
    (async () => {
      try {
        const { data } = await api.get(`/files/info/${path}`);
        if (!cancelled) setMeta(data);
      } catch {
        /* endpoint optional; ignore */
      }
    })();
    return () => {
      cancelled = true;
      if (localBlobUrl) {
        try { URL.revokeObjectURL(localBlobUrl); } catch { /* ignore */ }
      }
    };
  }, [path]);

  const download = async () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = path.split("/").pop() || "foto.jpg";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="photo-view">
      <header className="flex items-center justify-between px-4 py-3 bg-black/90 border-b border-neutral-800">
        <button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
          className="inline-flex items-center gap-2 text-sm opacity-80 hover:opacity-100"
          data-testid="photo-view-back"
        >
          <ArrowLeft className="w-4 h-4" /> Indietro
        </button>
        <div className="text-xs uppercase tracking-widest opacity-70">UNION CHECK</div>
        {blobUrl ? (
          <button
            onClick={download}
            className="inline-flex items-center gap-2 bg-[#ff5a00] hover:bg-[#ff7a30] text-white text-sm font-bold px-3 py-1.5 rounded-full"
            data-testid="photo-view-download"
          >
            <Download className="w-4 h-4" /> Scarica
          </button>
        ) : <div className="w-[84px]" />}
      </header>

      <main className="flex-1 flex items-center justify-center p-3 sm:p-6">
        {loading && (
          <div className="flex items-center gap-2 opacity-70">
            <Loader2 className="w-5 h-5 animate-spin" /> Caricamento foto…
          </div>
        )}
        {!loading && error && (
          <div className="max-w-md text-center" data-testid="photo-view-error">
            <AlertCircle className="w-10 h-10 mx-auto mb-3 text-[#ff3b30]" />
            <div className="font-bold text-lg">{error}</div>
            <div className="text-sm opacity-70 mt-1 break-all">{path}</div>
            <Link to="/" className="inline-block mt-4 text-[#ff5a00] hover:underline">
              Torna alla Dashboard
            </Link>
          </div>
        )}
        {!loading && !error && blobUrl && (
          <img
            src={blobUrl}
            alt="Foto"
            className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
            data-testid="photo-view-image"
          />
        )}
      </main>

      {meta && meta.item_id && (
        <footer className="px-4 py-3 bg-black border-t border-neutral-800 text-sm text-center">
          <span className="opacity-60">Appartiene a: </span>
          <Link
            to={
              meta.item_type === "scaffold_revision"
                ? `/scaffolds/${meta.scaffold_id}`
                : `/devices/${meta.device_id}`
            }
            className="text-[#ff5a00] hover:underline font-semibold"
          >
            {meta.item_label}
          </Link>
        </footer>
      )}
    </div>
  );
}
